"use client";

import { Core } from "@faire/design-tokens";
import { useSettingProductTileIterationInTreatment } from "@faire/retailer-visitor-shared/settings/useSettingProductTileIteration";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled, { css } from "styled-components";

interface IProps {
  noPadding?: boolean;
}

export const PromotedBadge: React.FC<IProps> = ({ noPadding }) => {
  const { strictLocalize } = useStrictLocalization();
  const viewport = useViewport();
  const showTileIterationChanges = useSettingProductTileIterationInTreatment();

  if (viewport.isTabletAndBelow) {
    return (
      <BadgeContent
        noPadding={noPadding}
        showTileIterationChanges={showTileIterationChanges}
      />
    );
  }
  return (
    <StyledTooltip
      position="bottom"
      targetNode={
        <BadgeContent
          noPadding={noPadding}
          showTileIterationChanges={showTileIterationChanges}
        />
      }
      contents={[
        {
          title: undefined,
          body: strictLocalize({
            defaultMessage:
              "You’re seeing this promoted product based on its relevance to your search query or browsing history on Faire.",
            description: {
              text: "This is the content of a tooltip to explain why this product is promoted by Faire",
            },
          }),
        },
      ]}
    />
  );
};

const BadgeContent = ({
  noPadding,
  showTileIterationChanges,
}: {
  noPadding: boolean | undefined;
  showTileIterationChanges: boolean;
}) => (
  <StyledPromotedBadge
    variant={
      showTileIterationChanges ? "paragraphSansRegular" : "labelSansRegular"
    }
    color={Core.text.subdued}
    $noPadding={noPadding}
  >
    <StrictLocalMsg
      defaultMessage="Promoted"
      description={{
        text: "This is a text below a product tile to show that this product is promoted by Faire.",
      }}
    />
  </StyledPromotedBadge>
);

const StyledTooltip = styled(Tooltip)`
  width: fit-content;
`;

const StyledPromotedBadge = styled(Typography)<{ $noPadding?: boolean }>`
  ${(props) =>
    !props.$noPadding
      ? css`
          padding: 4px 0;
        `
      : ""}
`;
